import { OperationReportTypeEnum } from '@shared/api/types';
import { Icons } from '@shared/components/content/icons';
import Text from '@shared/components/content/text';
import TextLink from '@shared/components/content/text-link';
import GuideTemplate from '@shared/components/guide-template';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { getReportsPathMap } from '../utils';
import { CreateReportContext } from './create-report-layout';

function ListItem({
  icon,
  children,
}: {
  icon: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div className="flex items-center space-x-3 rounded-lg bg-teal-50 px-4 py-2 shadow-sm">
      <div className="shrink-0">{icon}</div>
      <Text className="text-center font-light">{children}</Text>
    </div>
  );
}

export default function CreateReportIntroPage() {
  const { reportType } = useOutletContext<CreateReportContext>();
  const navigate = useNavigate();

  return (
    <GuideTemplate
      preTitle={`New ${reportType} report`}
      title="Creating a report"
      description={
        <div className="space-y-4">
          <Text>
            Reports are based on {reportType} data you have imported into your
            account. If you haven&apos;t already, you can start by uploading
            your data{' '}
            <TextLink
              to={
                getReportsPathMap()[reportType as OperationReportTypeEnum]
                  .imports
              }
            >
              here
            </TextLink>
            .
          </Text>
        </div>
      }
      primaryActionProps={{
        onClick: () =>
          navigate(
            getReportsPathMap()[reportType as OperationReportTypeEnum].setup
          ),
        'data-testid': 'get-started-button',
      }}
      primaryActionText="Get started"
      secondaryActionProps={{
        onClick: () =>
          navigate(
            getReportsPathMap()[reportType as OperationReportTypeEnum].list
          ),
      }}
      secondaryActionText="Back"
    >
      <div className="my-4 max-w-md space-y-5">
        <ListItem icon={<Icons.tags className="size-6 text-teal-500" />}>
          Start by selecting which data to include in your report, for example
          by site, time period, or custom tags.
        </ListItem>
        <ListItem icon={<Icons.barChart2 className="size-6 text-teal-500" />}>
          You can view your report online, download and share with others, and
          download an export of the emissions data.
        </ListItem>
        <ListItem icon={<Icons.refreshCcw className=" size-6 text-teal-500" />}>
          If you import new data matching the report tags, you can simply
          republish to include the new data in your report.
        </ListItem>
      </div>
    </GuideTemplate>
  );
}
